import type { PmDoc, PmNode } from "@storytell/types/richText";
import type { Editor, Extension } from "@tiptap/core";
import Dropcursor from "@tiptap/extension-dropcursor";
import Link from "@tiptap/extension-link";
import StarterKit from "@tiptap/starter-kit";
import { type Component, type JSX, createRenderEffect, on } from "solid-js";
import { EditorContent, createEditor } from "tiptap-solid";

import { CustomCodeBlock } from "./extensions/CodeBlockLowlight.extension";
import { Heading } from "./extensions/Heading.extension";
import { VariableV1 } from "./extensions/variableV1/VariableV1.extension";

import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import AutoJoiner from "tiptap-extension-auto-joiner";
import GlobalDragHandle from "tiptap-extension-global-drag-handle";

import "./extensions/DragHandle.css";
import "./styles/hljs-github.css";
import "./styles/hljs-github-dark.css";
import type { GlobalEventData } from "@storytell/types";
import { EditorBubbleMenu } from "./EditorBubbleMenu";
import { CitationExtension, type CitationNodeProps } from "./extensions/citation";

export const RichTextEditor: Component<{
  doc: PmDoc;
  onQuoteInPromptBar?: (data: GlobalEventData<"quote-in-prompt-bar">) => void;
  onEditorReady?: (editor: Editor) => void;
  citationRenderer: (props: CitationNodeProps) => JSX.Element;
  editable?: boolean;
}> = (props) => {
  const editor = createEditor({
    editorProps: {
      attributes: {
        class: "outline-none px-4",
      },
      editable: () => props.editable ?? true,
    },
    extensions: [
      VariableV1,
      CitationExtension({ renderer: props.citationRenderer }),
      StarterKit.configure({
        bulletList: {
          HTMLAttributes: {
            class: "list-disc pl-4",
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: "list-decimal pl-4",
          },
        },
        listItem: {
          HTMLAttributes: {
            class: "[&_p]:mb-4",
          },
        },
        code: {
          HTMLAttributes: {
            class: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-sm",
          },
        },
        codeBlock: false,
        blockquote: {
          HTMLAttributes: {
            class: "border-l-2 border-indigo-100 dark:border-indigo-700 pl-2 py-1 my-1",
          },
        },
        heading: false,
        strike: {
          HTMLAttributes: {
            class: "line-through",
          },
        },
        italic: {
          HTMLAttributes: {
            class: "italic",
          },
        },
        bold: {
          HTMLAttributes: {
            class: "font-bold",
          },
        },
        dropcursor: false,
      }) as Extension,
      Heading,
      CustomCodeBlock,
      Dropcursor.configure({
        class: "dropcursor",
        color: "#3b82f6",
        width: 2,
      }),
      Table.configure({
        allowTableNodeSelection: true,
        resizable: true,
        cellMinWidth: 0,
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: "border-b border-muted-border [&:last-child]:border-b-0",
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: "bg-panel whitespace-nowrap px-2 py-2 text-sm border-r border-muted-border [&:last-child]:border-r-0",
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: "px-2 py-2 border-r border-muted-border [&:last-child]:border-r-0",
        },
      }),
      Link.configure({
        HTMLAttributes: {
          class: "text-brand underline underline-offset-2",
        },
      }),
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      GlobalDragHandle.configure({}) as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      AutoJoiner as any,
    ],
    content: props.doc,
    onCreate({ editor }) {
      props.onEditorReady?.(editor);
      if (props.editable ?? true) {
        editor.commands.setTextSelection((editor.state.doc.content.firstChild?.nodeSize || 0) - 1);
        editor.commands.focus();
      }
    },
  });

  createRenderEffect(
    on(
      () => props.doc,
      (doc) => {
        try {
          editor()?.commands.setContent(doc);
        } catch (error) {
          console.error("Failed to set content in editor:", error);
        }
      },
    ),
  );

  return (
    <>
      <EditorContent
        class={`max-w-full
        [&>.ProseMirror>p]:mb-4
        [&_.tableWrapper]:overflow-x-auto [&_.tableWrapper]:w-full [&_.tableWrapper]:max-w-full
        [&_.tableWrapper]:border-1 [&_.tableWrapper]:rounded-lg
        [&_.tableWrapper]:border-muted-border
`}
        editor={editor()}
      />
      <EditorBubbleMenu editor={editor()} onQuoteInPromptBar={props.onQuoteInPromptBar} />
    </>
  );
};
